import { AssessmentType } from "./assessment";
import { ITEM_WEIGHT_URL, ORDINAL_VALUE_URL } from "./extensions";

export type ScoreProgressDirection = "HIGHER_IS_BETTER" | "LOWER_IS_BETTER";

export type DisplayOption = "SCORE_ONLY" | "SCORE_SLASH_MAX";

interface SingleScoreConfig {
	displayOption: DisplayOption;
	/**
	 * Maximum possible total score, `null` when the score is unbounded, or a FHIRPath expression for questionnaires where
	 * the applicable maximum depends on which sections were actually completed.
	 */
	max: number | null | { expression: string };
	/** FHIRPath expression evaluated against the QuestionnaireResponse. */
	expression: string;
}

export interface ScoreConfig extends SingleScoreConfig {
	progress?:
		| {
				type: "STRING_SCORE";
				orderedValues: string[];
				progressDirection: ScoreProgressDirection;
		  }
		| {
				type: "NUMBER_SCORE";
				progressDirection: ScoreProgressDirection;
		  }
		| {
				type: "RANKED_SCORE";
				rank: (rankParams: { score: number; max: number }) => number;
		  };
	component?: (SingleScoreConfig & { title: { expression: string } })[];
}

/**
 * Sums the `itemWeight` / `ordinalValue` extension values carried on the selected `answer.valueCoding`. Works for any
 * choice-based questionnaire whose answers carry one of those extensions.
 */
const WEIGHTED_CODING_SUM = `repeat(item).answer.valueCoding.extension.where(
	url = '${ORDINAL_VALUE_URL}'
	or url = '${ITEM_WEIGHT_URL}'
).valueDecimal.sum()`;

/**
 * Sums the `itemWeight` / `ordinalValue` extension values for the answers at the given `linkIds`. For questionnaires
 * where the score is the sum of a specific subset of choice items.
 */
const weightedCodingSumForItems = (linkIds: string[]) => {
	const collection = linkIds.map((id) => `'${id}'`).join(" | ");
	return `repeat(item).where(linkId in (${collection})).answer.valueCoding.extension.where(
	url = '${ORDINAL_VALUE_URL}'
	or url = '${ITEM_WEIGHT_URL}'
).valueDecimal.sum()`;
};

/**
 * Picks the `valueInteger` answer at `linkId`. For questionnaires with a single required integer answer (e.g. seconds
 * elapsed).
 */
const singleIntegerAnswer = (linkId: string) => `repeat(item).where(linkId = '${linkId}').answer.valueInteger`;

/**
 * Picks the `valueInteger` answer at `linkId`, falling back to 0 while the item is unanswered. For sections whose
 * points are entered as a count: without the fallback the empty answer would propagate through the surrounding
 * arithmetic and blank out the whole total instead of just that section.
 */
const singleIntegerAnswerOrZero = (linkId: string) => {
	const answer = singleIntegerAnswer(linkId);
	return `iif((${answer}).exists(), ${answer}, 0)`;
};

const textFromItemWithLinkId = (linkId: string) => `repeat(item).where(linkId = '${linkId}').text`;

/** Whether any of the given items carries an answer. For sections that are only scored once the user has engaged them. */
const anyItemAnswered = (linkIds: string[]) => {
	const collection = linkIds.map((id) => `'${id}'`).join(" | ");
	return `repeat(item).where(linkId in (${collection})).answer.exists()`;
};

/**
 * Whether the item at `linkId` was answered with the given `answerOption` code. For questionnaires where one answer
 * selects which of several branches applies.
 */
const answeredWithCoding = (linkId: string, code: string) =>
	`repeat(item).where(linkId = '${linkId}').answer.valueCoding.where(code = '${code}').exists()`;

/**
 * The MMSE items that always contribute to the score: orientation-time (40100) and orientation-place (40700) at five
 * points each, registration (41300) and recall (43500) at three each, and language (43900) at nine — 25 of the 30
 * points. The three registration words themselves (41400/41500/41600) are free text carrying no weight, so they only
 * count through the "was it repeated correctly?" questions that follow them.
 */
const MMSE_ALWAYS_SCORED_ITEMS = [
	"40200",
	"40300",
	"40400",
	"40500",
	"40600",
	"40800",
	"40900",
	"41000",
	"41100",
	"41200",
	"41700",
	"41800",
	"41900",
	"43600",
	"43700",
	"43800",
	"44000",
	"44100",
	"44200",
	"44300",
	"44400",
	"44500",
	"44600",
	"44700",
	"44800",
];

/**
 * Serial sevens ("Rekenvaardigheid", 42200) — the attention branch selected by answering 42100 with 42101. Like the
 * spelling branch below, the assessor enters how many answers were correct (42300, 0-5) rather than judging each
 * subtraction separately.
 */
const MMSE_SERIAL_SEVENS_EXPRESSION = singleIntegerAnswerOrZero("42300");

/**
 * Spelling a word backwards ("Spelling test", 42800) — the attention branch selected by answering 42100 with 42102. The
 * assessor enters how many letters were named correctly (43000, 0-5), so the branch scores its own points directly
 * rather than through weighted answers.
 */
const MMSE_SPELLING_EXPRESSION = singleIntegerAnswerOrZero("43000");

/**
 * The remaining 5 points come from the attention and calculation section (42000), which offers the two mutually
 * exclusive branches above. Only the branch actually selected at 42100 is counted, mirroring the `enableWhen` the
 * questionnaire puts on both sub-groups: a plain sum over every weighted answer would double-count once an assessor
 * switches branch mid-fill and the abandoned branch keeps its answers.
 *
 * Yields 0 rather than empty while no branch is selected, so it stays addable to the rest of the total — the other
 * sections' points still show up in the score as the assessment is filled in.
 */
const MMSE_ATTENTION_EXPRESSION = `iif(${answeredWithCoding("42100", "42101")},
	${MMSE_SERIAL_SEVENS_EXPRESSION},
	iif(${answeredWithCoding("42100", "42102")}, ${MMSE_SPELLING_EXPRESSION}, 0)
)`;

/**
 * MNA is two-stage: a 14-point screening ("Controle", 50100), followed — only when `enableWhenExpression` finds the
 * screening score ≤11 — by a 16-point full assessment ("Onderzoek", 50800), for a 30-point combined total. When
 * screening alone scores 12–14, the protocol says the full assessment isn't needed, so no total out of 30 exists for
 * that response; the total and its max fall back to the screening subtotal (out of 14) in that case.
 */
const MNA_SCREENING_ITEMS = ["50200", "50300", "50400", "50500", "50600", "50700"];
const MNA_SCREENING_EXPRESSION = weightedCodingSumForItems(MNA_SCREENING_ITEMS);

/** The individually-weighted scoring items of the MNA full assessment section ("Onderzoek", 50800). */
const MNA_ASSESSMENT_WEIGHTED_ITEMS = [
	"50900",
	"51000",
	"51100",
	"51200",
	"51700",
	"51800",
	"51900",
	"52000",
	"52100",
	"52200",
	"52300",
];

/** The three yes/no items of the protein-intake group (51300), which score as a group rather than individually. */
const MNA_PROTEIN_INTAKE_ITEMS = ["51400", "51500", "51600"];

/**
 * The "Geselecteerde consumptie indicatoren voor eiwit inname" group (51300) scores 0, 0.5 or 1 point based on how many
 * of its three yes/no items (51400/51500/51600) were answered "Ja" (51401/51501/51601) — those answer options carry no
 * `itemWeight`/`ordinalValue`, so the generic weighted-sum expression can't pick this up.
 */
const MNA_PROTEIN_INTAKE_YES_COUNT = `repeat(item).where(linkId in (${MNA_PROTEIN_INTAKE_ITEMS.map(
	(id) => `'${id}'`,
).join(" | ")})).answer.valueCoding.where(
	code in ('51401' | '51501' | '51601')
).count()`;
const MNA_PROTEIN_INTAKE_EXPRESSION = `iif(${MNA_PROTEIN_INTAKE_YES_COUNT} = 3, 1, iif(${MNA_PROTEIN_INTAKE_YES_COUNT} = 2, 0.5, 0))`;

/**
 * Whether the conditionally-enabled full assessment section ("Onderzoek", 50800) has been engaged at all.
 *
 * Deliberately checks _every_ scoring item in the section rather than just its first question: the section's items can
 * be answered in any order, and gating on one question would drop the whole section's points from the total while its
 * own subscore still counted them — reporting e.g. a total of 6/14 alongside an Onderzoek subscore of 13/16.
 */
const MNA_ASSESSMENT_ANSWERED_EXPRESSION = anyItemAnswered([
	...MNA_ASSESSMENT_WEIGHTED_ITEMS,
	...MNA_PROTEIN_INTAKE_ITEMS,
]);

/**
 * The questionnaire's actual `enableWhenExpression` gate on the full assessment section: all six screening items must
 * be answered, and their weighted score must be ≤11. Mirrors the FHIRPath baked into `questionnaire-MNA.*.json`'s
 * `enableWhenExpression` extension, so the total/max calculated here agree with which section the questionnaire itself
 * exposes for answering.
 */
const MNA_SCREENING_GATE_EXPRESSION = `${MNA_SCREENING_ITEMS.map(
	(id) => `repeat(item).where(linkId = '${id}').answer.exists()`,
).join(" and ")} and (${MNA_SCREENING_EXPRESSION}) <= 11`;

/**
 * Whether the total/max should follow the full-assessment (30-point) path: the section must actually have been answered
 * _and_ the screening gate that unlocks it must hold. Checking only {@link MNA_ASSESSMENT_ANSWERED_EXPRESSION} would let
 * a screening score of 12-14 — where the protocol says the full assessment isn't needed — still add assessment points
 * and claim a max of 30 if that section happened to carry stray answers.
 */
const MNA_FULL_ASSESSMENT_APPLIES_EXPRESSION = `(${MNA_ASSESSMENT_ANSWERED_EXPRESSION}) and (${MNA_SCREENING_GATE_EXPRESSION})`;

/** The full assessment subtotal, as a raw sum. Only meaningful once {@link MNA_ASSESSMENT_ANSWERED_EXPRESSION} holds. */
const MNA_ASSESSMENT_SUM_EXPRESSION = `(${weightedCodingSumForItems(
	MNA_ASSESSMENT_WEIGHTED_ITEMS,
)}) + (${MNA_PROTEIN_INTAKE_EXPRESSION})`;

/**
 * The full assessment subscore. Yields an empty result — rendered as "-" rather than "0" — when the section was never
 * engaged, so the screening-only pathway doesn't show a misleading 0/16 for an assessment that was never carried out.
 */
const MNA_ASSESSMENT_EXPRESSION = `iif(${MNA_ASSESSMENT_ANSWERED_EXPRESSION}, ${MNA_ASSESSMENT_SUM_EXPRESSION}, {})`;

/**
 * MNA's clinical categories aren't ordered by raw score magnitude, because the score isn't on one scale: a
 * screening-only "normal" result (12-14/14, full assessment not needed) is a _better_ outcome than even a "normal
 * nutritional status" full-assessment result (24-30/30) — reaching the full assessment at all already means
 * malnutrition risk was flagged at screening. Ranks run from worst (0) to best (3); trend comparison treats a higher
 * rank as always better, regardless of the raw score difference.
 */
const MNA_PROGRESS_RANK = (params: { score: number; max: number }): number => {
	if (params.max === 14) {
		return 3; // Screening-only: risk indicator stayed clear, full assessment wasn't needed.
	}
	if (params.score >= 24) {
		return 2; // Full assessment: normal nutritional status.
	}
	if (params.score >= 17) {
		return 1; // Full assessment: at risk of malnutrition.
	}
	return 0; // Full assessment: malnutrition.
};

const SCORE_CONFIGS: Partial<Record<AssessmentType, ScoreConfig>> = {
	BRADEN: {
		progress: { type: "NUMBER_SCORE", progressDirection: "HIGHER_IS_BETTER" },
		max: 23,
		expression: WEIGHTED_CODING_SUM,
		displayOption: "SCORE_SLASH_MAX",
	},
	"DOLOPLUS-2": {
		progress: { type: "NUMBER_SCORE", progressDirection: "LOWER_IS_BETTER" },
		max: 30,
		expression: WEIGHTED_CODING_SUM,
		displayOption: "SCORE_SLASH_MAX",
	},
	NORTON: {
		progress: { type: "NUMBER_SCORE", progressDirection: "HIGHER_IS_BETTER" },
		max: 20,
		expression: WEIGHTED_CODING_SUM,
		displayOption: "SCORE_SLASH_MAX",
	},
	OHAT: {
		progress: { type: "NUMBER_SCORE", progressDirection: "LOWER_IS_BETTER" },
		max: 16,
		expression: WEIGHTED_CODING_SUM,
		displayOption: "SCORE_SLASH_MAX",
	},
	"PACSLAC-D": {
		progress: { type: "NUMBER_SCORE", progressDirection: "LOWER_IS_BETTER" },
		max: 24,
		expression: WEIGHTED_CODING_SUM,
		displayOption: "SCORE_SLASH_MAX",
		component: [
			{
				title: { expression: textFromItemWithLinkId("70100") },
				max: 10,
				expression: weightedCodingSumForItems([
					"70200",
					"70300",
					"70400",
					"70500",
					"70600",
					"70700",
					"70800",
					"70900",
					"71000",
					"71100",
				]),
				displayOption: "SCORE_SLASH_MAX",
			},
			{
				title: { expression: textFromItemWithLinkId("71200") },
				max: 6,
				expression: weightedCodingSumForItems(["71300", "71400", "71500", "71600", "71700", "71800"]),
				displayOption: "SCORE_SLASH_MAX",
			},
			{
				title: { expression: textFromItemWithLinkId("71900") },
				max: 8,
				expression: weightedCodingSumForItems(["72000", "72100", "72200", "72300", "72400", "72500", "72600", "72700"]),
				displayOption: "SCORE_SLASH_MAX",
			},
		],
	},
	PAINAD: {
		progress: { type: "NUMBER_SCORE", progressDirection: "LOWER_IS_BETTER" },
		max: 10,
		expression: WEIGHTED_CODING_SUM,
		displayOption: "SCORE_SLASH_MAX",
	},
	IDDSI: {
		progress: {
			type: "STRING_SCORE",
			progressDirection: "HIGHER_IS_BETTER",
			orderedValues: ["0", "1", "2", "3", "4", "5", "6", "EC7", "7"],
		},
		max: 7,
		expression: `repeat(item).where(linkId = '30200').answer.valueCoding.code.select(
  iif($this = '30201', '7',
    iif($this = '30202', 'EC7',
      iif($this = '30203', '6',
        iif($this = '30204', '5',
          iif($this = '30205', '4',
            iif($this = '30206', '3',
              iif($this = '30207', '2',
                iif($this = '30208', '1',
                  iif($this = '30209', '0', {})
                )
              )
            )
          )
        )
      )
    )
  )
)`,
		displayOption: "SCORE_ONLY",
	},
	TIMED_CHAIR_STAND: {
		progress: { type: "NUMBER_SCORE", progressDirection: "LOWER_IS_BETTER" },
		max: null,
		expression: singleIntegerAnswer("80200"),
		displayOption: "SCORE_ONLY",
	},
	TIMED_GET_UP_AND_GO: {
		progress: { type: "NUMBER_SCORE", progressDirection: "LOWER_IS_BETTER" },
		max: null,
		expression: singleIntegerAnswer("90200"),
		displayOption: "SCORE_ONLY",
	},
	TINETTI: {
		progress: { type: "NUMBER_SCORE", progressDirection: "HIGHER_IS_BETTER" },
		max: 28,
		expression: WEIGHTED_CODING_SUM,
		displayOption: "SCORE_SLASH_MAX",
		component: [
			{
				title: { expression: textFromItemWithLinkId("100100") },
				max: 16,
				expression: weightedCodingSumForItems([
					"100200",
					"100300",
					"100400",
					"100500",
					"100600",
					"100700",
					"100800",
					"101000",
					"101100",
					"101200",
				]),
				displayOption: "SCORE_SLASH_MAX",
			},
			{
				title: { expression: textFromItemWithLinkId("101300") },
				max: 12,
				expression: weightedCodingSumForItems([
					"101400",
					"101600",
					"101700",
					"101800",
					"101900",
					"102000",
					"102100",
					"102200",
					"102300",
					"102400",
				]),
				displayOption: "SCORE_SLASH_MAX",
			},
		],
	},
	VAS: {
		progress: { type: "NUMBER_SCORE", progressDirection: "LOWER_IS_BETTER" },
		max: 10,
		expression: singleIntegerAnswer("110200"),
		displayOption: "SCORE_ONLY",
	},

	MMSE: {
		progress: { type: "NUMBER_SCORE", progressDirection: "HIGHER_IS_BETTER" },
		max: 30,
		expression: `(${weightedCodingSumForItems(MMSE_ALWAYS_SCORED_ITEMS)}) + (${MMSE_ATTENTION_EXPRESSION})`,
		displayOption: "SCORE_SLASH_MAX",
	},

	MNA: {
		progress: { type: "RANKED_SCORE", rank: MNA_PROGRESS_RANK },
		max: { expression: `iif(${MNA_FULL_ASSESSMENT_APPLIES_EXPRESSION}, 30, 14)` },
		expression: `iif(
	${MNA_FULL_ASSESSMENT_APPLIES_EXPRESSION},
	(${MNA_SCREENING_EXPRESSION}) + (${MNA_ASSESSMENT_SUM_EXPRESSION}),
	${MNA_SCREENING_EXPRESSION}
)`,
		displayOption: "SCORE_SLASH_MAX",
		component: [
			{
				title: { expression: textFromItemWithLinkId("50100") },
				max: 14,
				expression: MNA_SCREENING_EXPRESSION,
				displayOption: "SCORE_SLASH_MAX",
			},
			{
				title: { expression: textFromItemWithLinkId("50800") },
				max: 16,
				expression: MNA_ASSESSMENT_EXPRESSION,
				displayOption: "SCORE_SLASH_MAX",
			},
		],
	},
} as const;

/** Returns the score config for the given assessment type, or `undefined` when none is registered. */
export const findScoreConfigFor = (assessmentType: AssessmentType): ScoreConfig | undefined =>
	SCORE_CONFIGS[assessmentType];

/**
 * Returns the score config for the given assessment type.
 *
 * @throws When no score config is registered for the type.
 */
export const getScoreConfigFor = (assessmentType: AssessmentType): ScoreConfig => {
	const config = SCORE_CONFIGS[assessmentType];
	if (!config) {
		throw new Error(`No score config registered for assessment type "${assessmentType}"`);
	}
	return config;
};
